/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/named */
import { addDays, formatISO } from 'date-fns';
import { useMemo } from 'react';
import useSWR from 'swr';
import { formatDisplayDate, getDayOffTime } from '@/helpers';
import { client, fetcher } from '@/plugins/graphql';

import { SchoolTournament, SchoolAmateurTournament } from '@/types/SchoolTournament';

type UseSchoolTournament = [
  {
    schoolTournament: SchoolTournament | null | undefined;
    businessHours: string[];
    mutate: any;
  },
  {
    create: (data: SchoolTournament, isChangeDate: boolean) => Promise<any>;
    createTournamentAttendance: (tournamentPlayers: ObjectIds[], eventDates: ObjectIds[]) => Promise<any>;
    updateAmateurTournament: (data: SchoolAmateurTournament) => Promise<any>;
  },
];
type ObjectIds = {
  id?: number | undefined;
};
const useSchoolTournament = (
  schoolTournamentId: string | string[] | undefined,
  schoolId?: number | null,
): UseSchoolTournament => {
  const query = useMemo(() => {
    return `
      {
        admin_school_tournament(where: {school: {id: {_eq: "${schoolId}"}}, id: {_eq: ${schoolTournamentId}}}) {
          id
          end_time
          imgs
          max_people
          name
          price
          price_for_non_member
          selection_criteria
          start_date
          start_time
          school_id
          description
          end_date
          venue
          apply_deadline
          payment_setting
          cancellation_deadline
          is_publish
          is_result_publish
          is_jta
          is_agreed
          amateur_tournament_id
          payment_deadline
          is_amateur_tournament
          is_input_school_name
          tournament_players {
            id
            amount
          }
          tournament_event_dates_aggregate(where: {tournament_games_aggregate: {count: {predicate: {_gt: 0}}}}) {
            aggregate {
              count
            }
          }
          school_tournaments(order_by: {created_at: desc}) {
            id
            imgs
            name
            start_date
            end_date
            apply_deadline
            start_time
            end_time
            tournament_groups {
              id
              group_name
            }
          }
        }
      }
    `;
  }, [schoolId, schoolTournamentId, fetcher]);

  const { data, mutate } = useSWR(schoolTournamentId ? query : null, fetcher, {
    revalidateOnFocus: false,
  });

  const create = async (data: SchoolTournament, isChangeDate: boolean) => {
    const eventDateInsert = [];
    const startDate = new Date(data.start_date);
    const differenceInDays = getDayOffTime(new Date(data.start_date), new Date(data.end_date));

    for (let i = 0; i <= differenceInDays; i++) {
      const nexDate = addDays(startDate, i);
      eventDateInsert.push(`
      {
        event_date: "${formatDisplayDate(nexDate)}", 
        school_tournament_id: ${data.id}
      }
    `);
    }

    const mutation = `
      mutation CreateBasicSetting {
        insert_admin_school_tournament(
          objects: {
                name: "${data.name}", 
                max_people: ${data.max_people}, 
                imgs: "{${data.imgs}}", 
                ${data.id ? `id: "${data.id}",` : ''}
                description: """${data.description}""", 
                cancellation_deadline: "${data.cancellation_deadline}", 
                price: ${data.price}, 
                price_for_non_member: ${data.price_for_non_member}, 
                selection_criteria: ${data.selection_criteria}, 
                start_date: "${data.start_date}", 
                end_date: "${data.end_date}", 
                start_time: "${data.start_time}", 
                venue: "${data.venue}", 
                school_id: ${data.school_id}, 
                end_time: "${data.end_time}",
                apply_deadline: "${data.apply_deadline}"
                payment_setting: "{${data.payment_setting}}", 
                is_publish: ${data.is_publish},
                is_result_publish: ${data.is_result_publish},
                is_jta: ${data.is_jta},
                payment_deadline: "${data.payment_deadline || ''}",
                is_agreed: ${data.is_agreed},
                is_input_school_name: ${data.is_input_school_name}
            }, on_conflict: {
                constraint: school_tournament_pkey, 
                update_columns: [
                    name, 
                    max_people, 
                    imgs, 
                    description, 
                    cancellation_deadline, 
                    price, 
                    price_for_non_member,
                    selection_criteria, 
                    start_date, 
                    end_date,
                    start_time,
                    end_time,
                    apply_deadline,
                    payment_setting,
                    is_publish,
                    is_result_publish,
                    is_jta,
                    payment_deadline,
                    is_agreed,
                    is_input_school_name
                    venue]}) {
                      returning {
                        id
                      }
          }
          ${
            data.id && isChangeDate
              ? `
              update_admin_tournament_event_date_many(
                updates: {
                  where: {
                    school_tournament_id: {_eq: ${data.id}}
                  }, 
                  _set: {deleted_at: "${formatISO(new Date())}"}}) {
                returning {
                  id
                }
              }

              insert_admin_tournament_event_date(
                objects: [${eventDateInsert.join(',')}], 
                on_conflict: {
                constraint: tournament_event_date_pkey, 
                update_columns: event_date
               }) {
               returning {
                 id
                }
              }
          `
              : ''
          }
      }
    `;

    try {
      return await client.request(mutation);
    } catch (error) {
      throw error;
    }
  };

  const createTournamentAttendance = async (tournamentPlayers: ObjectIds[], eventDates: ObjectIds[]) => {
    const tournamentAttendanceInsert = [];

    for (let i = 0; i < tournamentPlayers.length; i++) {
      for (let j = 0; j < eventDates.length; j++) {
        tournamentAttendanceInsert.push(`
        {
          attendance_status: 1,
          tournament_player_id: "${tournamentPlayers[i].id}", 
          tournament_event_date_id: ${eventDates[j]?.id}
        }
        `);
      }
    }

    const mutation = `
      mutation CreateTournamentPlayerAttendance {
        insert_admin_tournament_player_attendance(
          objects: [${tournamentAttendanceInsert}], 
          on_conflict: {constraint: tournament_player_attendance_pkey}) {
          returning {
            id
          }
        }
      }
    `;

    try {
      return await client.request(mutation);
    } catch (error) {
      throw error;
    }
  };

  const updateAmateurTournament = async (data: SchoolAmateurTournament) => {
    const mutation = `
      mutation updateAmateurTournament {
        update_admin_school_tournament(
          where: {id: {_eq: ${data.id}}},
          _set: {
            name: "${data.name}",
            is_publish: ${data.is_publish},
            is_result_publish: ${data.is_result_publish}
          }) {
          returning {
            id
          }
        }
      }
    `;

    try {
      return await client.request(mutation);
    } catch (error) {
      throw error;
    }
  };

  const schoolTournament: SchoolTournament | null | undefined = useMemo(() => {
    if (!data?.[`admin_school_tournament`]) {
      return undefined;
    } else if (data?.[`admin_school_tournament`].length === 0) {
      return null;
    } else {
      const schoolData: SchoolTournament = data?.[`admin_school_tournament`]?.[0];
      return schoolData;
    }
  }, [data]);

  // BusinessHour を string 型からオブジェクトに変換
  const businessHours = useMemo(() => {
    if (schoolTournament) {
      const startTime = schoolTournament.start_time.split(':');
      const endTime = schoolTournament.end_time.split(':');

      const startTimeStamp = Number(startTime[0]) * 60 + Number(startTime[1]);
      const endTimeStamp = Number(endTime[0]) * 60 + Number(endTime[1]);

      const diffTime = Math.ceil((endTimeStamp - startTimeStamp) / 60);

      const hours = [];

      for (let index = Number(startTime[0]); index <= Number(startTime[0]) + diffTime; index++) {
        hours.push(index);
      }

      return hours.map((hour) => String(hour).padStart(2, '0'));
    } else {
      return ['09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24'];
    }
  }, [schoolTournament]);
  return [
    { schoolTournament, mutate, businessHours },
    { create, createTournamentAttendance, updateAmateurTournament },
  ];
};

export default useSchoolTournament;
